import { useState } from 'react';
import toast from 'react-hot-toast';
import api, { sendReportEmail } from '../../../lib/api';
import { downloadBlob, readBlobError } from '../../../lib/downloadBlob';

/**
 * Report generation and delivery for a test session.
 *
 * Owns everything to do with turning a session into a downloadable/emailable
 * artifact: CSV/PDF export, PDF blob building, and the email-report modal state
 * plus its send flow. Extracted from TestSessionView so the orchestrator no
 * longer carries report plumbing. Behaviour is unchanged from the inline versions.
 *
 * @param {object} params
 * @param {string|number|null} params.sessionId
 */
export function useSessionReports({ sessionId }) {
  const [emailModal, setEmailModal] = useState({ open: false, reportType: 'session' });

  const handleExportCsv = async () => {
    if (!sessionId) return;
    const loadingId = toast.loading('Generating report...');
    try {
      const response = await api.get(`/sessions/${sessionId}/export`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      let filename = 'test_report.csv';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (fileNameMatch && fileNameMatch.length === 2) filename = fileNameMatch[1];
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.dismiss(loadingId);
      toast.success('Report downloaded!');
    } catch (err) {
      console.error('Download failed', err);
      toast.dismiss(loadingId);
      toast.error(err.message || 'Failed to download report');
    }
  };

  const handleExportPdf = async () => {
    if (!sessionId) return;
    const loadingId = toast.loading('Generating PDF...');
    try {
      const response = await api.get(`/sessions/${sessionId}/report.pdf`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || 'Test_Results_Report.pdf';
      downloadBlob(response.data, filename);
      toast.dismiss(loadingId);
      toast.success('PDF downloaded!');
    } catch (err) {
      console.error('PDF export failed', err);
      toast.dismiss(loadingId);
      toast.error(await readBlobError(err, 'Failed to download PDF'));
    }
  };

  const handleExportDefectReport = async () => {
    if (!sessionId) return;
    const loadingId = toast.loading('Generating Defect Report...');
    try {
      const response = await api.get(`/sessions/${sessionId}/defect-report.pdf`, { responseType: 'blob' });
      const contentDisposition = response.headers['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch?.[1] || 'Defect_Report.pdf';
      downloadBlob(response.data, filename);
      toast.dismiss(loadingId);
      toast.success('Defect report downloaded!');
    } catch (err) {
      console.error('Defect report failed', err);
      toast.dismiss(loadingId);
      toast.error(await readBlobError(err, 'Failed to download defect report'));
    }
  };

  const buildReportBlob = async (reportType) => {
    if (!sessionId) throw new Error('Missing session id');

    // Full Test Results and Defect reports are server-rendered (same PDF the
    // download buttons use). Unknown types throw — the old jsPDF repair path is gone.
    if (reportType === 'session') {
      const response = await api.get(`/sessions/${sessionId}/report.pdf`, { responseType: 'blob' });
      const contentDisposition = response.headers?.['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      return { blob: response.data, fileName: filenameMatch?.[1] || 'Test_Results_Report.pdf' };
    }

    if (reportType === 'defect') {
      const response = await api.get(`/sessions/${sessionId}/defect-report.pdf`, { responseType: 'blob' });
      const contentDisposition = response.headers?.['content-disposition'];
      const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
      return { blob: response.data, fileName: filenameMatch?.[1] || 'Defect_Report.pdf' };
    }

    throw new Error(`Unknown report type: ${reportType}`);
  };

  const openEmailModal = (reportType) => {
    setEmailModal({ open: true, reportType });
  };

  const handleSendEmailReport = async (recipientEmail, reportTypeOverride) => {
    const reportType = reportTypeOverride ?? emailModal.reportType;
    const loadingId = toast.loading('Sending report...');
    try {
      const { blob, fileName } = await buildReportBlob(reportType);
      await sendReportEmail({
        reportType,
        sessionId,
        recipientEmail,
        pdfBlob: blob,
        fileName,
      });
      toast.dismiss(loadingId);
      toast.success(`Report sent to ${recipientEmail}`);
    } catch (err) {
      toast.dismiss(loadingId);
      const apiError = err.response?.data;
      const msg = [apiError?.error, apiError?.hint, apiError?.code ? `(${apiError.code})` : '']
        .filter(Boolean)
        .join(' ');
      toast.error(msg || 'Failed to send report');
      throw err;
    }
  };

  return {
    emailModal,
    setEmailModal,
    handleExportCsv,
    handleExportPdf,
    handleExportDefectReport,
    openEmailModal,
    handleSendEmailReport,
  };
}
